import Axios from 'axios';
import { showToast } from "vant";
import {useHHttp} from "@/hooks/useHHttp.js"
import {log, kDebug} from "@/components/utils/HComFunctions.js";
import {hsp} from "@/components/utils/HSp.js";
import {hdi} from "@/components/utils/HDeviceInfo.js";
import HNewGameUIConfig from "@/components/utils/HNewGameUIConfig.js";

class MgSelectLinesLogic {
    // 单例模式，保证只创建一次实例
    static instance;

    static getInstance(callback) {
        if (!this.instance) {
            this.instance = new MgSelectLinesLogic(callback);
        }
        return this.instance;
    }

    constructor(callback) {
        this.preLineList = [
            "https://www.game250522linecfg.xyz",
            "https://www.ylhg250627linecfg.xyz",
            "https://www.tt250627linecfg.xyz",
            "https://line.yongli250627linecfg.xyz"
        ];
        this.helpLines = [
            "https://gitee.com/lspxgit/yongli/raw/main/helpline.txt",
            "https://raw.githubusercontent.com/kaerkaerkaerzzz/yongli/main/helpline.txt"
        ];

        this.fastLineUrl = "";
        this.findFastLineTimer = null;
        // DL-06 看门狗：一次性定时器，选线开始挂上，到点仍没选到网关则弹失败提示。
        this._watchdogTimer = null;
        this._givenUp = false;
        // M5: give-up 后不永久锁死——起低频恢复重试让网络恢复能自愈; 失败 toast 只弹一次。
        this._recoveryTimer = null;
        this._toastedGiveUp = false;
        this.dio = Axios.create({
            baseURL: '',
            timeout: 4000,
        });

        this.dio.defaults.headers.common['content-type'] = 'text/plain';

        this.tryCount = 0;

        // 获取本地线路数据
        let localLines = hsp.getStringList("SELECT_LINE_LIST");
        if (localLines) {
            this.processUpdateLine(localLines);
        } else {
            hsp.setStringList("SELECT_LINE_LIST", this.preLineList);
        }

        this.successCallback = callback;

        // RB-06: 注册运行时网关失效自愈钩子(幂等——每次构造都指向同一静态方法)。
        useHHttp().setGatewayFailover(MgSelectLinesLogic.reselectSilently);

        this._startSelection();
    }

    // 发起一轮选线: 密集探测(formHeaderTodo) + 一次性 30s 看门狗。构造与 give-up 后的恢复重试都走它。
    _startSelection() {
        this.formHeaderTodo();
        // DL-06：选线开始挂看门狗(30s)，到点仍没选到网关就 give-up 并起恢复重试。
        if (this._watchdogTimer) clearTimeout(this._watchdogTimer);
        this._watchdogTimer = setTimeout(() => {
            if (useHHttp().getServerUrlsConfig().ApiGw === "" && !this._givenUp) {
                this._giveUpSelectingLine();
            }
        }, 30000);
    }

    static _failoverRunning = false;

    // RB-06 运行时网关失效自愈(专用最小实现)。**刻意不复用启动选线机器**——后者选到网关会
    // /app/device/upload、successCallback(可能导航)、失败时弹"请检查网络后重启"toast, 且"先清空
    // 网关再选"会让探测窗口内所有业务请求打到空地址失败; 都不适合运行期静默自愈。
    // 本方法只用独立 axios 拉候选线路的 /apigw/cfg 汇总网关、探测 /api-speed, 只在探到存活网关后
    // 原子热替换 serverUrlsConfig; 全程不清空当前网关、不导航、不 toast、不埋点; 找不到就保持原网关。
    static async reselectSilently() {
        if (MgSelectLinesLogic._failoverRunning) return;
        MgSelectLinesLogic._failoverRunning = true;
        try {
            const probe = Axios.create({baseURL: '', timeout: 3000});
            probe.defaults.headers.common['content-type'] = 'text/plain';
            const lines = (hsp.getStringList("SELECT_LINE_LIST") || []).filter(e => e && `${e}`.trim() !== "");
            // ① 并发拉各候选线路的网关列表。
            const gateways = [];
            await Promise.all(lines.map(async (line) => {
                try {
                    const r = await probe.get(`${line}/apigw/cfg?t=${Date.now()}${Math.random().toString(36).slice(2, 5)}`);
                    if (r.status === 200 && Array.isArray(r.data)) gateways.push(...r.data);
                } catch (e) { /* ignore */ }
            }));
            // ② 并发探测网关 /api-speed, 探到第一台存活的就原子热替换(全程不清空当前网关)。
            let swapped = false;
            await Promise.all(gateways.map(async (gw) => {
                if (swapped || !gw || !gw.ApiGw) return;
                try {
                    const s = await probe.get(`${gw.ApiGw}/api-speed?t=${Date.now()}${Math.random().toString(36).slice(2, 5)}`);
                    if (!swapped && s.status === 200 && s.data && s.data.Code === 0) {
                        swapped = true;
                        useHHttp().setServerUrlsConfig(gw);
                    }
                } catch (e) { /* ignore */ }
            }));
        } catch (e) {
            /* 静默: 失败保持原网关不动, 下一轮连续失败会再次触发 */
        } finally {
            MgSelectLinesLogic._failoverRunning = false;
        }
    }

    // DL-06 放弃选线：置位并清理所有定时器，弹一次失败提示。收口无限静默重试。
    _giveUpSelectingLine() {
        this._givenUp = true;
        if (this.findFastLineTimer) {
            clearInterval(this.findFastLineTimer);
            this.findFastLineTimer = null;
        }
        if (this._watchdogTimer) {
            clearTimeout(this._watchdogTimer);
            this._watchdogTimer = null;
        }
        // 失败提示只弹一次(后续恢复重试静默, 不刷屏)。
        if (!this._toastedGiveUp) {
            this._toastedGiveUp = true;
            showToast("网络线路连接失败，请检查网络后重启");
        }
        // M5 修复: 不永久锁死。隔 30s 重新发起一轮完整选线(含新看门狗), 让网络恢复后能自愈,
        // 无需用户杀进程重启; 若期间已选到网关则不再重试。
        if (this._recoveryTimer) clearTimeout(this._recoveryTimer);
        this._recoveryTimer = setTimeout(() => {
            if (useHHttp().getServerUrlsConfig().ApiGw !== "") return;
            this._givenUp = false;
            this._startSelection();
        }, 30000);
    }

    // 复位探测状态：清最快线路、复位网关配置(全键空值)、清定时器。
    // 每条失败/重启路径先调它，否则守卫恒非空、所有重试空转（DL-01/DL-03）。
    // 注意：不复位 tryCount——让升级到 helpline 的计数跨重启累积，避免无界活锁(IX-03)。
    _resetProbeState() {
        this.fastLineUrl = "";
        useHHttp().setServerUrlsConfig({ ApiGw: "", AliasApiGw: "" });
        if (this.findFastLineTimer) {
            clearInterval(this.findFastLineTimer);
            this.findFastLineTimer = null;
        }
    }

    // /apigw/cfg 拿不到时的有界恢复：线路通但网关列表持续失败，累加计数，
    // 超阈值升级到 helpline 兜底(换线路列表)，否则复位重探——避免无界重试(IX-03)。
    _apigwFailedRetry() {
        this.tryCount++;
        if (this.tryCount > 2) {
            this.tryCount = 0;
            this.checkHelp();
        } else {
            this.formHeaderTodo();
        }
    }

    // 定时获取最快线路
    formHeaderTodo() {
        this._resetProbeState();
        // DL-06：已放弃则不再重启探测，挡住 5s收口/checkHelp 重入，收口无限重试。
        if (this._givenUp) return;
        this.findFastLine();
        if (this.findFastLineTimer) {
            clearInterval(this.findFastLineTimer);
        }
        this.findFastLineTimer = setInterval(() => {
            if (this.fastLineUrl === "") {
                this.findFastLine();
                this.tryCount++;
                if (this.tryCount > 2) {
                    clearInterval(this.findFastLineTimer);
                    this.findFastLineTimer = null;
                    this.checkHelp();
                    this.tryCount = 0;
                }
            }
        }, 3000);
    }

    // 检查帮助线路
    checkHelp() {
        for (let i = 0; i < this.helpLines.length; i++) {
            this.testSpeed({"ApiGw": this.helpLines[i], "AliasApiGw": ""}, true, true).then(async (obj) => {
                try {
                    const response = await this.dio.get(obj.ApiGw);
                    if (response.status === 200) {
                        const allLines = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
                        if (Array.isArray(allLines)) {
                            this.processUpdateLine(allLines);
                            this.formHeaderTodo();
                        }
                    }
                } catch (e) {
                    log(e.stack);
                }
            });
        }

        setTimeout(() => {
            if (this.fastLineUrl === "") {
                this.formHeaderTodo();
            }
        }, 5000);
    }

    // 获取最快线路
    findFastLine() {
        for (let i = 0; i < this.preLineList.length; i++) {
            this.testSpeed({"ApiGw": this.preLineList[i], "AliasApiGw": ""},false,true).then(async (obj) => {
                if (obj.ApiGw && obj.ApiGw["ApiGw"] !== "" && this.fastLineUrl === "") {
                    this.fastLineUrl = obj.ApiGw;
                    clearInterval(this.findFastLineTimer);
                    this.findFastLineTimer = null;
                    this.updateNewLineList();
                    this.requestServerList();
                }
            });
        }
    }

    // 请求服务器列表
    requestServerList() {
        this.dio.get(`${this.fastLineUrl}/apigw/cfg?t=${Date.now()}${Math.random().toString(36).slice(2, 5)}`)
            .then(response => {
                if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
                    this.findFastServerInto(response.data);
                    return;
                }
                // 非200 / 非数组 / 空数组：此刻定时器已被取消，若不重启则零定时器永久卡死(DL-02)；
                // 走有界升级而非直接重探，避免无界活锁(IX-03)。
                this._apigwFailedRetry();
            })
            .catch((e) => {
                log(e.stack);
                this._apigwFailedRetry();
            });
    }

    // 更新线路列表
    updateNewLineList() {
        this.dio.get(`${this.fastLineUrl}/line/cfg?t=${Date.now()}${Math.random().toString(36).slice(2, 5)}`)
            .then(response => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    const newLineList = response.data;
                    this.processUpdateLine(newLineList);
                }
            })
            .catch((e) => {
                log(e.stack);
            });
    }

    // 更新线路信息
    processUpdateLine(newLineList) {
        newLineList.forEach(line => {
            if (!this.preLineList.includes(line)) {
                this.preLineList.push(line);
            }
        });
        hsp.setStringList("SELECT_LINE_LIST", this.preLineList);
    }

    // 测试线路速度
    async testSpeed(obj, onlySpeed =false, isPingLine = false) {
        try {
            const response = await this.dio.get(`${obj.ApiGw}/api-speed?t=${Date.now()}${Math.random().toString(36).slice(2, 5)}`);
            if (onlySpeed) {
                return obj;
            } else {
                if (response.status === 200 && response.data.Code === 0) {
                    return obj;
                } else {
                    return {"ApiGw": "", "AliasApiGw": ""};
                }
            }
        } catch (e) {
            if (onlySpeed) {
                return obj;
            } else {
                return {
                    "ApiGw": "",
                    "AliasApiGw": "",
                };
            }
        }
    }

    // 根据速度找出最快的服务器
    findFastServerInto(serverList) {
        serverList.forEach(async (server) => {
            const obj = await this.testSpeed(server);
            if (obj.ApiGw && obj.ApiGw["ApiGw"] !== "" && useHHttp().getServerUrlsConfig().ApiGw === "") {
                useHHttp().setServerUrlsConfig(obj);
                //测试服
                let devStr = hsp.getMap("DEV_DEBUG");
                if (devStr != null) {
                    hdi.channelFlag = HNewGameUIConfig.newGameUIType === 0
                        ? "devv"
                        : hdi.channelFlag;
                    useHHttp().setServerUrlsConfig(devStr);
                }
                // 开发环境下，URL query 中的 host 参数优先级最高
                if (kDebug) {
                    const urlParams = new URLSearchParams(window.location.search);
                    const hostParam = urlParams.get('host');
                    if (hostParam) {
                        useHHttp().setServerUrlsConfig({
                            "ApiGw": hostParam,
                            "AliasApiGw": "URL参数指定"
                        });
                    }
                }
                //~
                if (this.findFastLineTimer) {
                    clearInterval(this.findFastLineTimer);
                    this.findFastLineTimer = null;
                }
                // DL-06：选到网关成功，正常离开选线阶段，取消看门狗+恢复重试。
                if (this._watchdogTimer) {
                    clearTimeout(this._watchdogTimer);
                    this._watchdogTimer = null;
                }
                if (this._recoveryTimer) {
                    clearTimeout(this._recoveryTimer);
                    this._recoveryTimer = null;
                }

                let thirdCode = localStorage.getItem("thirdCode");
                if (thirdCode == null) {
                    thirdCode = "";
                }

                useHHttp().request("/app/device/upload", () => {
                    },
                    {
                        params: {
                            "Did": hdi.deviceId,
                            "Code": HNewGameUIConfig.newGameUIType === 0
                                ? hdi.inviteCode
                                : "",
                            "PlatTypeNum": hdi.platTypeNo,
                            "ThirdCode":HNewGameUIConfig.newGameUIType === 0
                                ? ""
                                : thirdCode,
                        },
                        isShowLoading: false
                    }).then(() => {
                });
                if (this.successCallback != null) {
                    this.successCallback();
                }
            }
        });

        setTimeout(() => {
            if (useHHttp().getServerUrlsConfig().ApiGw === "") {
                this._apigwFailedRetry();
            }
        }, 5000);
    }
}

export default MgSelectLinesLogic;
